import { computed, inject, Injectable, signal } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';

export const MODAL_IDS = {
  PIIT_TRANSFER: 'piit-transfer',
  TOUR_WELCOME: 'tour-welcome',
  PUSH_NOTIFICATION: 'push-notification',
  CONTACT_INFO_REVIEW: 'contact-info-review',
  PAPERLESS_PROMPT: 'paperless-prompt',
  HELOC_ELIGIBILITY: 'heloc-eligibility',
  SATISFACTION_SURVEY: 'satisfaction-survey',
} as const;

export const MODAL_PRIORITIES: Record<string, number> = {
  [MODAL_IDS.PIIT_TRANSFER]: 1,
  [MODAL_IDS.PUSH_NOTIFICATION]: 2,
  [MODAL_IDS.TOUR_WELCOME]: 3,
  [MODAL_IDS.CONTACT_INFO_REVIEW]: 4,
  [MODAL_IDS.PAPERLESS_PROMPT]: 5,
  [MODAL_IDS.HELOC_ELIGIBILITY]: 6,
  [MODAL_IDS.SATISFACTION_SURVEY]: 7,
};

interface QueuedModal {
  id: string;
  priority: number;
}

@Injectable({ providedIn: 'root' })
export class ModalQueueService {
  private readonly router = inject(Router);
  private readonly queue = signal<QueuedModal[]>([]);
  private readonly _paused = signal(false);

  /** When paused, activeModal returns null so no modal is shown. */
  readonly activeModal = computed(() => {
    if (this._paused()) return null;
    const items = this.queue();
    return items.length > 0 ? items[0].id : null;
  });

  constructor() {
    this.router.events
      .pipe(filter((event): event is NavigationStart => event instanceof NavigationStart))
      .subscribe(() => this.clearAll());
  }

  enqueue(id: string, priority: number): void {
    this.queue.update((items) => {
      const filtered = items.filter((item) => item.id !== id);
      const updated = [...filtered, { id, priority }];
      updated.sort((a, b) => a.priority - b.priority);
      return updated;
    });
  }

  dismiss(id: string): void {
    this.queue.update((items) => items.filter((item) => item.id !== id));
  }

  isActive(id: string): boolean {
    return this.activeModal() === id;
  }

  clearAll(): void {
    this.queue.set([]);
  }

  /** Pause modal display (e.g. during the guided tour). */
  pause(): void {
    this._paused.set(true);
  }

  /** Resume modal display. */
  resume(): void {
    this._paused.set(false);
  }
}
